import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Skeleton } from '@ibsys/kit';
import { defaultThreshold, isThresholdApplied, useStrategistHierarchy } from '@/entities/strategist';
import type {
  StrategistHierarchyCluster,
  StrategistHierarchyRequest,
  StrategistTargetAttribute,
} from '@/entities/strategist';
import { ConceptCardDrawer, useOpenConceptCard } from '@/features/concept-card';
import { DialogsDrawerContent, useOpenDialogsDrawer } from '@/features/dialogs-drawer';
import { PageContainer } from '@/shared/ui';
import { StrategistFilters } from '@/widgets/StrategistFilters';
import type { StrategistFiltersValue } from '@/widgets/StrategistFilters';
import { HierarchyControls } from './HierarchyControls';
import { HierarchyKpi } from './HierarchyKpi';
import { HierarchyTable } from './HierarchyTable';

/**
 * Страница «Иерархия понятий» раздела «Стратег»: концепты выбранной категории
 * (роль: возражение / аргумент / вопрос), кластеризованные в иерархию по порогу
 * близости. Держит сквозной фильтр, целевую метрику, категорию и порог; на каждом
 * срезе отдаёт таблицу кластеров с медоидом, числом понятий, диалогов, долей
 * целевого исхода (вероятность выхода на успех) с влиянием и примерами формулировок.
 * Категория/порог инициализируются из первого ответа (порог — средний из набора).
 */
export const StrategistHierarchyPage: FC = () => {
  const { t } = useTranslation(['common', 'strategist']);

  // Состояние, влияющее на запрос.
  const [filterValue, setFilterValue] = useState<StrategistFiltersValue>({ filters: [] });
  const [target, setTarget] = useState<StrategistTargetAttribute>({ retention: true });
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [threshold, setThreshold] = useState<number | undefined>(undefined);

  const body: StrategistHierarchyRequest = useMemo(
    () => ({
      filters: filterValue.filters,
      dateFrom: filterValue.dateFrom,
      dateTo: filterValue.dateTo,
      targetAttribute: target,
      category,
      threshold,
    }),
    [filterValue, target, category, threshold],
  );

  const { data, isLoading, isPlaceholderData } = useStrategistHierarchy(body);

  // Показывать можно только данные, посчитанные теми же параметрами, что уйдут в срез строки:
  // пока идёт запрос за новыми или порог ещё не подставлен, таблица и панель диалогов разойдутся.
  const isAwaitingData = isLoading || isPlaceholderData || !isThresholdApplied(data?.thresholds, [threshold]);

  // Категория и порог берутся из первого ответа: доступные значения известны только по нему.
  useEffect(() => {
    if (!data) return;
    if (data.categories.length > 0) setCategory((prev) => prev ?? data.category);
    setThreshold((prev) => prev ?? defaultThreshold(data.thresholds));
  }, [data]);

  const clusters = data?.clusters ?? [];

  const handleConceptOpen = useOpenConceptCard(filterValue);

  const openDialogs = useOpenDialogsDrawer({ ...filterValue, target });
  // Строка проваливает в свои диалоги: понятие раскрывается обратно в кластер на текущем
  // пороге, поэтому показатели диалогов совпадают с показателями строки.
  const handleRowOpen = useCallback(
    (cluster: StrategistHierarchyCluster) => {
      openDialogs({
        conceptId: cluster.medoidConceptId,
        conceptThreshold: data?.threshold,
        title: cluster.label,
      });
    },
    [openDialogs, data?.threshold],
  );

  return (
    <PageContainer className="flex flex-col gap-5">
      <h1 className="text-xl font-semibold text-foreground">{t('common:routes.strategistHierarchy')}</h1>

      <StrategistFilters value={filterValue} onChange={setFilterValue} />

      <HierarchyControls
        target={target}
        onTargetChange={setTarget}
        categories={data?.categories ?? []}
        category={category}
        onCategoryChange={setCategory}
        thresholds={data?.thresholds ?? []}
        threshold={threshold}
        onThresholdChange={setThreshold}
      />

      {isAwaitingData ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      ) : clusters.length === 0 ? (
        <Card>
          <CardContent className="p-10 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">{t('common:emptyTable')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          <HierarchyKpi
            totalDialogs={data?.totalDialogs ?? 0}
            baselineRate={data?.baselineRate ?? null}
            threshold={data?.threshold ?? 0}
            clusters={clusters.length}
          />
          <HierarchyTable clusters={clusters} onRowOpen={handleRowOpen} onConceptOpen={handleConceptOpen} />
        </div>
      )}
      <ConceptCardDrawer />
      <DialogsDrawerContent />
    </PageContainer>
  );
};
